/**
 * R2-1/R2-2/R2-4 — version-scope hero step-funnel (ux-rework-plan.md §6 Phase
 * R2; approved mock overview-version-scope.html). Stacked converted/dropped
 * columns with per-step conversion labels, «→ %» arrows (worst step flagged),
 * click-a-step drill panel with top exits and a LIVE «Open in canvas» link
 * (PE-4: /onboarding-v2/:id?insights=1&focusScreen=<screenId>), and a
 * Funnel ⇄ Conversion-over-time ⇄ Table view toggle (table = the demoted
 * transitions table, passed as children).
 */
import React from 'react';
import { Alert, Box, Button, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useChartTheme } from '@modules/ui-components/hooks/useChartTheme';
import { FUNNEL_REPORTS_TEST_IDS } from '../testIds';
import { useFunnelUnit } from '../funnelUnit';
import { deriveFunnelSteps, deriveStepExits, type FunnelStepEdgeLike } from '../funnelSteps';
import { DataThroughCaption } from './DataThroughCaption';
import type { FunnelReportsDashboardData } from '../types';

export type FunnelHeroView = 'funnel' | 'trend' | 'table';

interface FunnelHeroSectionProps {
  edges: FunnelStepEdgeLike[];
  startedJourneys?: number | null;
  metricTimeseries: FunnelReportsDashboardData['metricTimeseries'];
  /** PE-4 canvas deep link for a screen; absent → the canvas button is hidden. */
  canvasHrefForScreen?: (screenId: string) => string;
  formatMetricLabel: (name: string) => string;
  /** R4-3: envelope freshness bound for the trend view's «Data through» caption. */
  dataThrough?: string;
  /** The demoted transitions table (current EvidenceTable), shown in Table view. */
  children: React.ReactNode;
}

const COLUMN_HEIGHT = 170;
const TREND_CHART_HEIGHT = 260;
const MAX_TREND_SERIES = 4;

const integerFormat = new Intl.NumberFormat('en-US');

function formatCount(value: number): string {
  return integerFormat.format(Math.round(value));
}

function formatShare(value: number | null): string {
  return value === null ? 'n/a' : `${(value * 100).toFixed(1)}%`;
}

export function FunnelHeroSection({
  edges,
  startedJourneys,
  metricTimeseries,
  canvasHrefForScreen,
  formatMetricLabel,
  dataThrough,
  children,
}: FunnelHeroSectionProps): React.ReactElement {
  const chart = useChartTheme();
  const funnelUnit = useFunnelUnit();
  const [view, setView] = React.useState<FunnelHeroView>('funnel');
  const [selectedScreenId, setSelectedScreenId] = React.useState<string | null>(null);

  const steps = React.useMemo(() => deriveFunnelSteps(edges, startedJourneys), [edges, startedJourneys]);
  const selectedStep = steps.find(step => step.screenId === selectedScreenId) ?? null;
  const selectedIndex = selectedStep ? steps.indexOf(selectedStep) : -1;
  const exits = React.useMemo(
    () => (selectedStep ? deriveStepExits(edges, steps, selectedStep.screenId) : []),
    [edges, steps, selectedStep],
  );

  // Worst between-step conversion gets the loud arrow (mock: «→ 17.4% ▼»).
  const worstArrowIndex = React.useMemo(() => {
    let worst = -1;
    for (let i = 1; i < steps.length; i++) {
      const rate = steps[i].stepConversion;
      if (rate === null) continue;
      if (worst === -1 || rate < (steps[worst].stepConversion ?? Number.POSITIVE_INFINITY)) worst = i;
    }
    return steps.length > 2 ? worst : -1;
  }, [steps]);

  const trendRows = React.useMemo(() => {
    const rateRows = metricTimeseries.filter(row => row.metricName.endsWith('_rate'));
    const names: string[] = [];
    const buckets = new Map<string, Record<string, number | string>>();
    for (const row of rateRows) {
      if (!names.includes(row.metricName)) {
        if (names.length >= MAX_TREND_SERIES) continue;
        names.push(row.metricName);
      }
      const bucket = row.bucketTs.slice(0, 16).replace('T', ' ');
      const item = buckets.get(bucket) ?? { bucket };
      item[row.metricName] = row.metricValue;
      buckets.set(bucket, item);
    }
    return { names, rows: [...buckets.values()] };
  }, [metricTimeseries]);

  return (
    <Stack spacing={1.5} data-testid={FUNNEL_REPORTS_TEST_IDS.funnelHero}>
      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <Typography variant="body2" color="text.secondary">
          Per-screen conversion — click a step to inspect its drop-off.
          {/* F2 honest deviation (FT-AC-087/D8): the dashboard's screen-flow
              edges have no person twin yet — in people mode the hero keeps
              its visits data and SAYS so, like the money lane; the people
              view of screen flow lives on the canvas report. */}
          {funnelUnit === 'people'
            ? ' Screen flow counts visits — open the canvas report for the people view.'
            : ''}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_event, next: FunnelHeroView | null) => next && setView(next)}
          data-testid={FUNNEL_REPORTS_TEST_IDS.funnelViewToggle}
        >
          <ToggleButton value="funnel" data-testid={FUNNEL_REPORTS_TEST_IDS.funnelView('funnel')}>Funnel</ToggleButton>
          <ToggleButton value="trend" data-testid={FUNNEL_REPORTS_TEST_IDS.funnelView('trend')}>Conversion over time</ToggleButton>
          <ToggleButton value="table" data-testid={FUNNEL_REPORTS_TEST_IDS.funnelView('table')}>Table</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {view === 'funnel' && (steps.length === 0 ? (
        <Alert severity="info">No screen transitions recorded for this scope and window.</Alert>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, 1fr)`, gap: 1.25, alignItems: 'end', px: 0.5, pt: 1 }}>
            {steps.map(step => {
              const selected = step.screenId === selectedScreenId;
              return (
                <Box
                  key={step.screenId}
                  role="button"
                  tabIndex={0}
                  data-testid={FUNNEL_REPORTS_TEST_IDS.funnelStep(step.screenId)}
                  onClick={() => setSelectedScreenId(current => (current === step.screenId ? null : step.screenId))}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedScreenId(current => (current === step.screenId ? null : step.screenId));
                    }
                  }}
                  sx={{ cursor: 'pointer', minWidth: 0, '&:focus-visible': { outline: theme => `2px solid ${theme.palette.primary.main}` } }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      height: COLUMN_HEIGHT,
                      borderRadius: '6px 6px 0 0',
                      bgcolor: alpha(chart.negative, 0.16),
                      overflow: 'hidden',
                      outline: selected ? `2px solid ${chart.primary}` : 'none',
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: `${Math.min(100, Math.max(0, (step.shareOfFirst ?? 0) * 100))}%`,
                        bgcolor: chart.primary,
                        opacity: selected ? 1 : 0.85,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" component="div" align="center" noWrap sx={{ fontWeight: 600, mt: 0.5 }} title={step.label}>
                    {step.label}
                  </Typography>
                  <Typography variant="caption" component="div" align="center" color="text.secondary">
                    {step.stepConversion === null
                      ? `${formatCount(step.entered)} entered`
                      : `${formatCount(step.entered)} · ${formatShare(step.shareOfFirst)}`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <Box
            data-testid={FUNNEL_REPORTS_TEST_IDS.funnelArrows}
            sx={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length}, 1fr)`, textAlign: 'center' }}
          >
            {steps.map((step, index) => (
              <Typography
                key={step.screenId}
                variant="caption"
                sx={{
                  color: index === worstArrowIndex ? chart.negative : 'text.secondary',
                  fontWeight: index === worstArrowIndex ? 700 : 400,
                }}
              >
                {index === 0 ? '' : `→ ${formatShare(step.stepConversion)}${index === worstArrowIndex ? ' ▼' : ''}`}
              </Typography>
            ))}
          </Box>

          {selectedStep && (
            <Box
              data-testid={FUNNEL_REPORTS_TEST_IDS.funnelDrill}
              sx={{
                borderLeft: `3px solid ${chart.primary}`,
                borderRadius: theme => `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
                bgcolor: 'action.hover',
                p: 1.75,
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Step «{selectedStep.label}» — drop-off
                  </Typography>
                  <Typography variant="caption" color="text.secondary">selected step</Typography>
                </Stack>
                {selectedIndex === steps.length - 1 ? (
                  <Typography variant="body2">
                    Final step — <Box component="b" sx={{ color: chart.positive }}>{formatCount(selectedStep.entered)} {selectedStep.entered === 1 ? 'journey' : 'journeys'} reached it</Box>{' '}
                    ({formatShare(selectedStep.shareOfFirst)} of funnel entrants).
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    <Box component="b" sx={{ color: chart.negative }}>{formatCount(selectedStep.droppedAfter)} {selectedStep.droppedAfter === 1 ? 'journey' : 'journeys'} dropped</Box>{' '}
                    ({formatShare(selectedStep.entered > 0 ? selectedStep.droppedAfter / selectedStep.entered : null)} of step entrants).
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" data-testid={FUNNEL_REPORTS_TEST_IDS.funnelDrillExits}>
                  {exits.length > 0
                    ? `Top exits: ${exits.map(exit => `${exit.label} (${formatShare(exit.share)})`).join(' · ')}`
                    : 'No outgoing transitions recorded from this step.'}
                </Typography>
                {canvasHrefForScreen && (
                  <Box>
                    <Button
                      size="small"
                      variant="outlined"
                      component={RouterLink}
                      to={canvasHrefForScreen(selectedStep.screenId)}
                      startIcon={<OpenInNewIcon />}
                      data-testid={FUNNEL_REPORTS_TEST_IDS.funnelCanvasLink}
                    >
                      Open in canvas
                    </Button>
                  </Box>
                )}
              </Stack>
            </Box>
          )}
        </>
      ))}

      {view === 'trend' && (trendRows.names.length === 0 ? (
        <Alert severity="info">No canonical conversion-rate series for this scope and window yet.</Alert>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={TREND_CHART_HEIGHT}>
            <LineChart data={trendRows.rows}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="bucket" minTickGap={18} tick={chart.axisTick} />
              <YAxis domain={[0, 1]} tickFormatter={(value: number) => `${Math.round(value * 100)}%`} tick={chart.axisTick} />
              <RechartsTooltip formatter={(value) => (typeof value === 'number' ? formatShare(value) : value)} />
              {trendRows.names.map((name, index) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  name={formatMetricLabel(name)}
                  stroke={chart.series[index % chart.series.length]}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <DataThroughCaption dataThrough={dataThrough} testId={FUNNEL_REPORTS_TEST_IDS.funnelTrendDataThrough} />
        </>
      ))}

      {view === 'table' && children}
    </Stack>
  );
}
